import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SideBar from "../components/SideBar";
import TopBar from "../components/TopBar";
import dots from "../assets/image/dots.svg";
import CustomModal from "../components/CustomModal";

export default function Notifications() {
  const [openMenu, setOpenMenu] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleMenu = (index) => setOpenMenu(openMenu === index ? null : index);
  const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          toast.error("Please log in to view your notifications.", {
            theme: "colored",
          });
          navigate("/login");
          return;
        }

        // console.log("📬 Fetching notifications...");

        const res = await fetch(`${TEST_BASE_URL}/notifications`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        // console.log("📡 Response Status:", res.status);

        const contentType = res.headers.get("content-type");
        let data;

        if (contentType && contentType.includes("application/json")) {
          data = await res.json();
        } else {
          const text = await res.text();
          console.error(" Non-JSON response:", text.slice(0, 200));
          toast.error("Unexpected server response", { theme: "colored" });
          return;
        }

        // console.log("✅ Notifications Data:", data);

        if (!res.ok) {
          toast.error(data.message || "Failed to load notifications.", {
            theme: "colored",
          });
          return;
        }

        // Handle multiple response structures
        let notificationsList = [];
        if (Array.isArray(data)) {
          notificationsList = data;
        } else if (data.data && Array.isArray(data.data)) {
          notificationsList = data.data;
        } else if (
          data.data &&
          data.data.notifications &&
          Array.isArray(data.data.notifications)
        ) {
          notificationsList = data.data.notifications;
        } else if (data.notifications && Array.isArray(data.notifications)) {
          notificationsList = data.notifications;
        }

        // console.log("📊 Parsed Notifications:", notificationsList);
        // console.log("📊 Notification Count:", notificationsList.length);

        if (notificationsList.length === 0) {
          console.warn("⚠️ No notifications found in response");
          console.log(
            "💡 Full response structure:",
            JSON.stringify(data, null, 2)
          );
        }

        setNotifications(notificationsList);
      } catch (error) {
        console.error(" Notifications fetch error:", error);
        toast.error("Network error — please try again.", { theme: "colored" });
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [navigate]);

  const deleteNotification = async () => {
    if (!selectedNotificationId) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem("authToken");

      // console.log("🗑️ Deleting notification:", selectedNotificationId);

      const res = await fetch(
        `${TEST_BASE_URL}/notifications/${selectedNotificationId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      // console.log("📡 Delete Response Status:", res.status);

      if (!res.ok) {
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          data = { message: "Failed to delete notification" };
        }
        throw new Error(data.message || "Failed to delete notification");
      }

      toast.success("Notification deleted successfully!", { theme: "colored" });
      setShowModal(false);

      // Remove from list
      setNotifications(
        notifications.filter((n) => n.id !== selectedNotificationId)
      );
      setSelectedNotificationId(null);
    } catch (error) {
      console.error(" Delete error:", error);
      toast.error(error.message || "Failed to delete notification", {
        theme: "colored",
      });
    } finally {
      setDeleting(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem("authToken");

      // console.log("📖 Marking notification as read:", notificationId);

      const res = await fetch(
        `${TEST_BASE_URL}/notifications/${notificationId}/mark-as-read`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      // console.log("📡 Mark as Read Response Status:", res.status);

      if (res.ok) {
        toast.success("Marked as read", { theme: "colored" });
        // Update notification in list
        setNotifications(
          notifications.map((n) =>
            n.id === notificationId ? { ...n, is_read: 1 } : n
          )
        );
      } else {
        console.warn("⚠️ Failed to mark as read");
      }
    } catch (error) {
      console.error(" Mark as read error:", error);
    }

    setOpenMenu(null);
  };

  const openDeleteModal = (notificationId) => {
    setSelectedNotificationId(notificationId);
    setShowModal(true);
  };

  const handleDoThisLater = () => setShowModal(false);
  const handleLetsDoThis = () => deleteNotification();

  const personIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 84 92"
      fill="none"
    >
      <path
        d="M73.875 18.375L71.2412 60.9816C70.5682 71.8674 70.2318 77.3103 67.5032 81.2234C66.1542 83.1581 64.4173 84.791 62.403 86.0181C58.329 88.5 52.8757 88.5 41.9692 88.5C31.0482 88.5 25.5878 88.5 21.511 86.0135C19.4954 84.7841 17.758 83.1484 16.4094 81.2106C13.6817 77.291 13.3527 71.8405 12.6946 60.9394L10.125 18.375"
        stroke="white"
        strokeWidth="6.375"
        strokeLinecap="round"
      />
      <path
        d="M3.75 18.375H80.25M59.2368 18.375L56.3355 12.3898C54.4082 8.41409 53.4446 6.42622 51.7823 5.18642C51.4136 4.91142 51.0231 4.66681 50.6148 4.45498C48.7741 3.5 46.565 3.5 42.1467 3.5C37.6175 3.5 35.3529 3.5 33.4817 4.49501C33.0669 4.71553 32.6712 4.97006 32.2985 5.25596C30.617 6.54598 29.6777 8.6066 27.7991 12.7278L25.2249 18.375"
        stroke="white"
        strokeWidth="6.375"
        strokeLinecap="round"
      />
      <path
        d="M31.375 65.125L31.375 39.625"
        stroke="white"
        strokeWidth="6.375"
        strokeLinecap="round"
      />
      <path
        d="M52.625 65.125L52.625 39.625"
        stroke="white"
        strokeWidth="6.375"
        strokeLinecap="round"
      />
    </svg>
  );
  return (
    <section className="dashboard">
      <SideBar isOpen={sidebarOpen} />
      <div className="body-content">
        <TopBar onToggleSidebar={toggleSidebar} />

        <div className="bottom-content">
          <div className="notification-wrap">
            <span>Notifications</span>
            <a href="/settings?section=notifications">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 22 22"
                fill="none"
              >
                <path
                  d="M19.8175 6.14139L19.3239 5.28479C18.9506 4.63696 18.764 4.31305 18.4464 4.18388C18.1288 4.05472 17.7696 4.15664 17.0513 4.36048L15.8311 4.70418C15.3725 4.80994 14.8913 4.74994 14.4726 4.53479L14.1357 4.34042C13.7766 4.11043 13.5004 3.77133 13.3475 3.37274L13.0136 2.37536C12.794 1.71534 12.6842 1.38533 12.4228 1.19657C12.1615 1.00781 11.8143 1.00781 11.1199 1.00781L10.0051 1.00781C9.31078 1.00781 8.9636 1.00781 8.70223 1.19657C8.44085 1.38533 8.33106 1.71534 8.11149 2.37536L7.77753 3.37274C7.62465 3.77133 7.34845 4.11043 6.98937 4.34042L6.65249 4.53479C6.23374 4.74994 5.75259 4.80994 5.29398 4.70418L4.07375 4.36048C3.35541 4.15664 2.99625 4.05472 2.67867 4.18388C2.36109 4.31305 2.17445 4.63696 1.80115 5.28479L1.30757 6.14139C0.957656 6.74864 0.782703 7.05227 0.816657 7.37549C0.850614 7.69871 1.08483 7.95918 1.55326 8.48012L2.5843 9.63282C2.8363 9.95185 3.01521 10.5078 3.01521 11.0077C3.01521 11.5078 2.83636 12.0636 2.58433 12.3827L1.55326 13.5354C1.08483 14.0564 0.850616 14.3168 0.816659 14.6401C0.782703 14.9633 0.957662 15.2669 1.30757 15.8741L1.80114 16.7307C2.17443 17.3785 2.36109 17.7025 2.67867 17.8316C2.99625 17.9608 3.35542 17.8589 4.07377 17.655L5.29394 17.3113C5.75263 17.2055 6.23387 17.2656 6.65267 17.4808L6.9895 17.6752C7.34851 17.9052 7.62464 18.2442 7.7775 18.6428L8.11149 19.6403C8.33106 20.3003 8.44085 20.6303 8.70223 20.8191C8.9636 21.0078 9.31078 21.0078 10.0051 21.0078H11.1199C11.8143 21.0078 12.1615 21.0078 12.4228 20.8191C12.6842 20.6303 12.794 20.3003 13.0136 19.6403L13.3476 18.6428C13.5004 18.2442 13.7765 17.9052 14.1356 17.6752L14.4724 17.4808C14.8912 17.2656 15.3724 17.2055 15.8311 17.3113L17.0513 17.655C17.7696 17.8589 18.1288 17.9608 18.4464 17.8316C18.764 17.7025 18.9506 17.3785 19.3239 16.7307L19.8175 15.8741Z"
                  stroke="black"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M14.0195 11C14.0195 12.933 12.4525 14.5 10.5195 14.5C8.58653 14.5 7.01953 12.933 7.01953 11C7.01953 9.067 8.58653 7.5 10.5195 7.5C12.4525 7.5 14.0195 9.067 14.0195 11Z"
                  stroke="#141B34"
                  strokeWidth="1.5"
                />
              </svg>
              Manage Notification Settings
            </a>
          </div>

          <div className="notification-content" ref={menuRef}>
            <div className="notificationtitle">
              <h6>All Notifications</h6>
            </div>

            {loading ? (
              <p className="text-center mt-5">Loading notifications...</p>
            ) : notifications.length === 0 ? (
              <p className="text-center mt-5 text-muted">
                You have no notifications yet.
              </p>
            ) : (
              notifications.map((n, i) => (
                <div key={n.id || i}>
                  <div className="notification-item">
                    <div className="notification-info">
                      <img src={n.icon || dots} alt="notification" width="40" />
                      <div className="notification-text">
                        <p className="notification-msg">
                          {n.message || n.title || "Notification"}
                        </p>
                        <small>
                          {n.created_at || n.timestamp || "Just now"}
                        </small>
                      </div>
                    </div>

                    <div className="notification-actions">
                      <img
                        src={dots}
                        alt="menu"
                        className="menu-btn"
                        onClick={() => toggleMenu(i)}
                      />
                      {openMenu === i && (
                        <div className="notification-menu">
                          <span onClick={() => markAsRead(n.id)}>
                            Mark as Read
                          </span>
                          <span onClick={() => openDeleteModal(n.id)}>
                            Delete Notification
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <hr />
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <CustomModal
        isOpen={showModal}
        title="Delete Notification?"
        message="Clear this notification - this action cannot be undone."
        secondaryText="Cancel"
        primaryText={deleting ? "Deleting..." : "Yes, Delete"}
        onPrimary={handleLetsDoThis}
        onSecondary={handleDoThisLater}
        icon={personIcon}
        disabled={deleting}
      />
    </section>
  );
}
